import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Clock, CreditCard, CheckCircle2 } from 'lucide-react';
import { auction as auctionApi } from '../services/api';

const left = (iso, now) => {
  const ms = new Date(iso.endsWith('Z') ? iso : `${iso}Z`) - now;
  if (ms <= 0) return 'Time expired';
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
  return h ? `${h}h ${m}m left` : `${m}m ${s}s left`;
};
const fmtDate = (iso) => iso ? new Date(iso.endsWith('Z') ? iso : `${iso}Z`).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const PendingRow = ({ w, now }) => (
  <li className="flex items-center gap-3 py-3" data-testid={`auction-win-pending-${w.id}`}>
    {w.image ? <img src={w.image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0"/> : <div className="w-12 h-12 rounded-lg bg-[#13261a] shrink-0"/>}
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-white truncate">{w.title}{w.isTest && <span className="ml-1 text-[10px] text-yellow-400">TEST</span>}</div>
      <div className="text-xs text-gray-300">Winning bid {Number(w.winningAmount || 0).toLocaleString()} pts · Due {money(w.checkoutTotal)}</div>
      {w.checkoutDeadline && <div className="text-xs text-orange-300 inline-flex items-center gap-1" data-testid="auction-win-timer"><Clock size={11}/> {left(w.checkoutDeadline, now)}</div>}
    </div>
    <Link to={w.checkoutUrl} data-testid={`auction-win-checkout-${w.id}`}
      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-green-600 hover:bg-green-500 text-white text-xs font-semibold">
      <CreditCard size={13}/> Checkout
    </Link>
  </li>
);

export const AuctionWinsCard = () => {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => { auctionApi.myWins().then(setData).catch(() => setData({ pending: [], completed: [] })); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  if (!data) return null;
  const empty = !data.pending.length && !data.completed.length;
  return (
    <div className="pbb-card p-5" data-testid="profile-auction-wins">
      <div className="flex items-center gap-2"><Trophy className="text-yellow-400" size={20}/><div className="font-medium text-white">Auction Wins</div></div>
      {empty ? (
        <p className="mt-2 text-xs text-gray-300" data-testid="auction-wins-empty">No wins yet. <Link to="/auction" className="text-green-400 underline">Browse the auction</Link> and place a bid.</p>
      ) : (
        <>
          {data.pending.length > 0 && (
            <ul className="mt-2 divide-y divide-[#1f1f1f]" data-testid="auction-wins-pending">
              {data.pending.map(w => <PendingRow key={w.id} w={w} now={now}/>)}
            </ul>
          )}
          {data.completed.length > 0 && (
            <div className="mt-3" data-testid="auction-wins-completed">
              <div className="text-[11px] uppercase tracking-widest text-gray-300 font-semibold">Completed Wins</div>
              <ul className="mt-1 divide-y divide-[#1f1f1f]">
                {data.completed.map(w => (
                  <li key={w.id} className="flex items-center gap-2 py-2 text-sm" data-testid={`auction-win-completed-${w.id}`}>
                    <CheckCircle2 size={14} className="text-green-400 shrink-0"/>
                    <span className="flex-1 truncate text-gray-100">{w.title}</span>
                    <span className="text-xs text-gray-300">{fmtDate(w.purchasedAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};
